import {
  ConfirmAgencySampleReportUploadMutation,
  GetAgencyTemplateByDomainQuery,
  RequestAgencySampleReportUploadMutation,
} from '@clipr/lib';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ApiError } from '../../api/apiError';
import { ApiService } from '../../api/apiService';
import { parseJson } from '../agencyDetails';
import { TrainedFormatSummary } from './trainedFormatPreview';

export type AgencySampleTemplate = NonNullable<
  GetAgencyTemplateByDomainQuery['getAgencyTemplateByDomain']
>;
export type AgencySampleConfirmedTemplate =
  ConfirmAgencySampleReportUploadMutation['confirmAgencySampleReportUpload'];
type AgencyUploadTicket = RequestAgencySampleReportUploadMutation['requestAgencySampleReportUpload'];

export type AgencySampleUploadContext = {
  orgId: string;
  domain: string;
  sampleReportS3Key?: string | null;
  sourceCustomSpecialtyId?: string | null;
  trainedFormat?: TrainedFormatSummary | null;
};

export type AgencySampleUploadOptions = {
  apiService: Pick<ApiService,
    'requestAgencySampleReportUpload' |
    'confirmAgencySampleReportUpload' |
    'getAgencyTemplateByDomain'>;
  getContext: () => AgencySampleUploadContext | null;
  onConfirmed?: (template: AgencySampleConfirmedTemplate) => void;
  onTrainingComplete: (template: AgencySampleTemplate) => Promise<void> | void;
  refreshTrainedFormat?: () => Promise<void>;
};

/** Training is a Bedrock round trip; polling faster just burns requests. */
const TRAINING_POLL_MS = 4000;
/** Past this, say so rather than let the user assume it has hung. */
const TRAINING_SLOW_MS = 60_000;
/** Past the backend's 900s ceiling, stop a dead run from spinning forever. */
const TRAINING_TIMEOUT_MS = 18 * 60_000;

/**
 * Shared state and mechanics for the agency-wide sample narrative uploader.
 *
 * The mutation identity and polling identity are deliberately separate: writes target an org id,
 * while the only template query that exposes upload tickets is keyed by domain.
 */
export class AgencySampleUpload {

  constructor(readonly options: AgencySampleUploadOptions) {
    makeObservable(this);
  }

  @observable isUploadingSample = false;
  @observable sampleUploadError: string | null = null;
  @observable.ref lastUploadTicket: AgencyUploadTicket | null = null;
  @observable isTrainingFormat = false;
  @observable trainingError: string | null = null;
  @observable isTrainingSlow = false;

  private trainingPollTimer: ReturnType<typeof setTimeout> | null = null;
  private trainingStartedAt = 0;
  private trainingPollSeq = 0;
  private operationSeq = 0;

  @computed
  get sampleStatus(): string {
    const context = this.options.getContext();
    if (!context)
      return 'Not loaded';

    const sampleReportS3Key = context.sampleReportS3Key
      ?? context.trainedFormat?.sampleReportS3Key;
    if (sampleReportS3Key && context.sourceCustomSpecialtyId)
      return 'Sample trained';
    if (sampleReportS3Key)
      return 'Sample uploaded';
    return 'Sample report needed';
  }

  @computed
  get trainedFormat(): TrainedFormatSummary | null {
    return this.options.getContext()?.trainedFormat ?? null;
  }

  @computed
  get canDownload(): boolean {
    return Boolean(this.options.refreshTrainedFormat);
  }

  @action
  async uploadSampleReport(file: File) {
    const context = this.captureContext();
    if (!context)
      return;

    this.stopTrainingPoll();
    const operationSeq = ++this.operationSeq;
    this.isUploadingSample = true;
    this.sampleUploadError = null;
    this.trainingError = null;

    const [ticketRes, ticketErr] = await this.options.apiService.requestAgencySampleReportUpload({
      orgId: context.orgId,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
    });

    if (!this.isCurrent(context, operationSeq))
      return;

    if (ticketErr) {
      runInAction(() => {
        if (!this.isCurrent(context, operationSeq)) return;
        this.isUploadingSample = false;
        this.sampleUploadError = ticketErr.message;
      });
      return;
    }

    const ticket = ticketRes?.requestAgencySampleReportUpload;
    if (!ticket) {
      runInAction(() => {
        if (!this.isCurrent(context, operationSeq)) return;
        this.isUploadingSample = false;
        this.sampleUploadError = 'Upload ticket was not returned.';
      });
      return;
    }

    runInAction(() => {
      if (this.isCurrent(context, operationSeq))
        this.lastUploadTicket = ticket;
    });

    const uploadError = await postSampleFile(ticket, file).catch((error: unknown) =>
      error instanceof Error ? error.message : 'Upload failed.');
    if (!this.isCurrent(context, operationSeq))
      return;
    if (uploadError) {
      runInAction(() => {
        if (!this.isCurrent(context, operationSeq)) return;
        this.isUploadingSample = false;
        this.sampleUploadError = uploadError;
      });
      return;
    }

    await this.confirmTicket(context, ticket.ticketId, operationSeq);
  }

  @action
  async confirmSampleUpload(ticketId?: string) {
    const context = this.captureContext();
    const resolvedTicketId = ticketId ?? this.lastUploadTicket?.ticketId;
    if (!context || !resolvedTicketId)
      return;

    this.stopTrainingPoll();
    const operationSeq = ++this.operationSeq;
    this.isUploadingSample = true;
    this.sampleUploadError = null;
    this.trainingError = null;
    await this.confirmTicket(context, resolvedTicketId, operationSeq);
  }

  private async confirmTicket(
    context: AgencySampleUploadContext,
    ticketId: string,
    operationSeq: number,
  ) {
    const [res, err] = await this.options.apiService.confirmAgencySampleReportUpload({
      orgId: context.orgId,
      ticketId,
    });
    if (!this.isCurrent(context, operationSeq))
      return;

    runInAction(() => {
      if (!this.isCurrent(context, operationSeq)) return;
      this.isUploadingSample = false;
      if (err) {
        this.sampleUploadError = isUploadNotFoundError(err)
          ? 'Upload not found. Confirm again after the upload completes.'
          : err.message;
        return;
      }

      const template = res?.confirmAgencySampleReportUpload;
      if (template)
        this.options.onConfirmed?.(template);
    });

    // A failed confirm never claimed a ticket, so there is nothing to poll.
    if (!err && this.isCurrent(context, operationSeq))
      this.watchTraining(context, ticketId, operationSeq);
  }

  @action
  private watchTraining(
    context: AgencySampleUploadContext,
    ticketId: string,
    operationSeq: number,
  ) {
    this.stopTrainingPoll();
    this.isTrainingFormat = true;
    this.isTrainingSlow = false;
    this.trainingError = null;
    this.trainingStartedAt = Date.now();
    const pollSeq = ++this.trainingPollSeq;

    const poll = async () => {
      if (!this.isCurrent(context, operationSeq) || pollSeq !== this.trainingPollSeq)
        return;

      const [res] = await this.options.apiService.getAgencyTemplateByDomain({ domain: context.domain });
      if (!this.isCurrent(context, operationSeq) || pollSeq !== this.trainingPollSeq)
        return;

      const template = res?.getAgencyTemplateByDomain ?? null;
      const ticket = readUploadTicket(template, ticketId);
      const elapsed = Date.now() - this.trainingStartedAt;
      const nonTerminal = !ticket
        || ticket.status === 'training' || ticket.status === 'pending' || ticket.status === 'uploaded';

      if (elapsed <= TRAINING_TIMEOUT_MS && nonTerminal) {
        runInAction(() => { this.isTrainingSlow = elapsed > TRAINING_SLOW_MS; });
        this.trainingPollTimer = setTimeout(() => void poll(), TRAINING_POLL_MS);
        return;
      }

      const confirmed = ticket?.status === 'confirmed';
      if (confirmed && template)
        await this.options.onTrainingComplete(template);
      if (!this.isCurrent(context, operationSeq) || pollSeq !== this.trainingPollSeq)
        return;

      runInAction(() => {
        this.isTrainingFormat = false;
        this.isTrainingSlow = false;
        this.trainingPollTimer = null;
        this.trainingError = confirmed
          ? null
          : ticket?.status === 'failed'
            ? (ticket.error ?? 'Training failed. Try uploading the sample again.')
            : 'Training is taking longer than expected. Reload the page to check, or upload the sample again.';
      });
    };

    this.trainingPollTimer = setTimeout(() => void poll(), TRAINING_POLL_MS);
  }

  @action
  async downloadSample() {
    if (!this.options.refreshTrainedFormat)
      return;

    const context = this.captureContext();
    if (!context)
      return;

    const operationSeq = this.operationSeq;
    // Keep the tab handle from the click gesture, then sever its opener before navigating away.
    const tab = window.open('', '_blank');
    await this.options.refreshTrainedFormat();
    if (!this.isCurrent(context, operationSeq)) {
      tab?.close();
      return;
    }

    const url = this.trainedFormat?.sampleReportUrl;
    if (tab && url) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      tab?.close();
    }
  }

  @action
  reset() {
    this.operationSeq++;
    this.stopTrainingPoll();
    this.isUploadingSample = false;
    this.sampleUploadError = null;
    this.lastUploadTicket = null;
    this.isTrainingFormat = false;
    this.trainingError = null;
    this.isTrainingSlow = false;
  }

  private stopTrainingPoll() {
    if (this.trainingPollTimer)
      clearTimeout(this.trainingPollTimer);
    this.trainingPollTimer = null;
    this.trainingPollSeq++;
  }

  private captureContext(): AgencySampleUploadContext | null {
    const context = this.options.getContext();
    return context ? { ...context } : null;
  }

  private isCurrent(context: AgencySampleUploadContext, operationSeq: number): boolean {
    const current = this.options.getContext();
    return operationSeq === this.operationSeq
      && current?.orgId === context.orgId
      && current.domain === context.domain;
  }
}

/** Reads one upload ticket out of an AWSJSON field returned as a string or parsed object. */
function readUploadTicket(
  template: { sampleUploadTickets?: unknown } | null,
  ticketId: string,
): { status?: string; error?: string } | undefined {
  const raw = template?.sampleUploadTickets;
  if (raw === undefined || raw === null) return undefined;
  let tickets: unknown = raw;
  if (typeof raw === 'string') {
    try {
      tickets = JSON.parse(raw);
    } catch {
      return undefined;
    }
  }
  if (typeof tickets !== 'object' || tickets === null) return undefined;
  const ticket = (tickets as Record<string, unknown>)[ticketId];
  if (typeof ticket !== 'object' || ticket === null) return undefined;
  const { status, error } = ticket as { status?: unknown; error?: unknown };
  return {
    status: typeof status === 'string' ? status : undefined,
    error: typeof error === 'string' ? error : undefined,
  };
}

async function postSampleFile(ticket: AgencyUploadTicket, file: File): Promise<string | null> {
  const formData = new FormData();
  const uploadFields = parseJson(ticket.uploadFields);

  if (Array.isArray(uploadFields)) {
    for (const field of uploadFields) {
      if (isUploadField(field))
        formData.append(field.key, field.value);
    }
  } else if (uploadFields && typeof uploadFields === 'object') {
    for (const [key, value] of Object.entries(uploadFields))
      formData.append(key, String(value));
  }

  formData.append('file', file);
  const response = await fetch(ticket.uploadUrl, { method: 'POST', body: formData });
  return response.ok ? null : `Upload failed with status ${response.status}.`;
}

function isUploadNotFoundError(error: ApiError): boolean {
  const graphQlErrors = error.data.graphQLError?.errors ?? [];
  return error.message.includes('UPLOAD_NOT_FOUND')
    || graphQlErrors.some((graphQlError) =>
      graphQlError.message.includes('UPLOAD_NOT_FOUND')
      || graphQlError.errorType === 'UPLOAD_NOT_FOUND');
}

function isUploadField(value: unknown): value is { key: string; value: string } {
  return Boolean(
    value
    && typeof value === 'object'
    && typeof (value as { key?: unknown }).key === 'string'
    && typeof (value as { value?: unknown }).value === 'string',
  );
}
